import { BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import type { SetupHealth } from '../../app/models/SetupHealth';
import { getDashboardSetupStatus } from '../setup/dashboardSetupStatus';

// This shop-scoped hint changes wording only. Visibility always uses fresh checks.
// Missing/blocked browser storage must not prevent setup checks from working.
const readyKey = (shop: string) => `partfits.setup-previously-ready:${shop.trim().toLowerCase()}`;
const previouslyReady = (shop: string) => {
    try { return Boolean(shop) && localStorage.getItem(readyKey(shop)) === 'true'; } catch { return false; }
};

export default function DashboardSetupCard() {
    const navigate = useNavigate();
    const [health, setHealth] = useState<SetupHealth>();
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        const load = async () => {
            try {
                // No selected theme ID: always ask for the published theme.
                const result = await agent.Settings.setupHealth();
                if (cancelled) return;
                setHealth(result);
                if (result.shopDomain && getDashboardSetupStatus(result).ready) {
                    try { localStorage.setItem(readyKey(result.shopDomain), 'true'); } catch { /* Optional wording hint. */ }
                }
            } catch {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [reload]);

    if (loading && !health && !reload) return null;
    const status = health && !failed ? getDashboardSetupStatus(health) : undefined;
    if (status?.ready && !loading) return null;
    const openSetup = () => navigate('/setup-health');
    const retry = () => setReload(value => value + 1);

    if (loading || failed || !status || status.pending.length === 0) {
        return <section aria-label="Setup verification"><InlineStack gap="300" blockAlign="center">
            <Text as="p" tone="subdued">{loading ? 'Checking setup…' : 'Setup status could not be verified.'}</Text>
            <Button variant="plain" loading={loading} onClick={retry}>Recheck setup</Button>
            <Button variant="plain" onClick={openSetup}>Setup &amp; installation</Button>
        </InlineStack></section>;
    }

    const needsReview = status.coreComplete || previouslyReady(health!.shopDomain);
    return <section aria-labelledby="pf-dashboard-setup-title"><Card><BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="300">
            <BlockStack gap="100">
                <Text as="h2" id="pf-dashboard-setup-title" variant="headingMd">
                    {needsReview ? 'Your setup needs attention' : 'Finish setting up PartFits'}
                </Text>
                <Text as="p" tone="subdued">{status.complete} of {status.total} required checks complete</Text>
            </BlockStack>
            <Button variant="primary" onClick={openSetup}>{needsReview ? 'Review setup' : 'Continue setup'}</Button>
        </InlineStack>
        <BlockStack gap="100">{status.pending.map(step => <Text key={step.label} as="p">{step.label}</Text>)}</BlockStack>
        {status.unverified && <InlineStack gap="200" blockAlign="center">
            <Text as="p" tone="subdued">Published theme checks are still unverified.</Text>
            <Button variant="plain" onClick={retry}>Recheck setup</Button>
        </InlineStack>}
    </BlockStack></Card></section>;
}
